import React, { useState, useEffect, useRef } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { FaExchangeAlt, FaDesktop } from 'react-icons/fa'
import { BiErrorCircle } from 'react-icons/bi'

import BarHeader from './common/BarHeader'
import Input from './iol_exchange/Input'
import Result from './iol_exchange/Result'
import ViewSwitch from './iol_exchange/inputs/ViewSwitch'
import CalculateBtn from './iol_exchange/CalculateBtn'
import Popup from './common/Popup'
import { calculateExchange } from './services/IOLExchange'
import { isSameModelChange, getToricBlocker, CURRENT_POWER_MIN, CURRENT_POWER_MAX, POWER_STEP } from './iol_exchange/IOLExchangeCalculation'
import './iol_exchange/iol-exchange.css'

// Phones, upright or sideways. The calculator is desktop-only for now: a phone
// gets a notice instead of the form.
const PHONE_QUERY = '(max-width: 768px) and (orientation: portrait), (max-height: 500px) and (orientation: landscape)';

export default function IOLExchangeCalculator() {

    const [showResult, setShowResult] = useState(false);
    const [validInput, setValidInput] = useState(false);
    const [showMissingPopup, setShowMissingPopup] = useState(false);

    // Result of the last calculation. [F1]–[F6] run on the server, against the
    // engine validated by the workbook fixtures — see services/IOLExchange.js.
    const [ladder, setLadder] = useState(null);
    const [calculating, setCalculating] = useState(false);
    const [calcError, setCalcError] = useState(null);

    // Large (default) vs Compact interface. Remembered per browser.
    const [compact, setCompact] = useState(() => localStorage.getItem('iolxCompact') === '1');
    useEffect(() => {
        localStorage.setItem('iolxCompact', compact? '1' : '0');
    }, [compact]);

    const [phone, setPhone] = useState(() => window.matchMedia(PHONE_QUERY).matches);
    useEffect(() => {
        const query = window.matchMedia(PHONE_QUERY);
        const onChange = (e) => setPhone(e.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    // Every calculation is stored, so a double click must not send it twice.
    // A ref, not the `calculating` state: clicks in the same tick all see the
    // state from before the first one.
    const inFlight = useRef(false);

    // // States for Form // //
    // Every state is [value, validity], as in the other calculators:
    // -1 empty / 0 invalid / 1 valid.

    // EXCHANGE SETUP //
    //Change IOL model?
    const [F11VS, setF11VS] = useState(["", -1]);
    //A-constant source
    const [F12VS, setF12VS] = useState(["Model list", 1]);

    // CURRENT IOL //
    //Power
    const [F21VS, setF21VS] = useState(["", -1]);
    //Manufacturer / Model
    const [F22VS, setF22VS] = useState(["Select implanted IOL model", -1]);
    //A-constant
    const [F23VS, setF23VS] = useState(["", -1]);
    //Position
    const [F24VS, setF24VS] = useState(["Bag", 1]);
    //Cylinder and axis of marks (toric calculation only)
    const [F25VS, setF25VS] = useState(["", -1]);
    const [F26VS, setF26VS] = useState(["", -1]);

    // CURRENT MANIFEST REFRACTION //
    //Sphere
    const [F31VS, setF31VS] = useState(["", -1]);
    //Cylinder
    const [F32VS, setF32VS] = useState(["", -1]);
    //MRSE (computed, read only)
    const [F33VS, setF33VS] = useState(["", -1]);
    //Axis (optional; required for a toric calculation)
    const [F34VS, setF34VS] = useState(["", -1]);

    // REASON FOR EXCHANGE //
    //Reasons (multi-select)
    const [F41VS, setF41VS] = useState([[], -1]);
    //Other (free text)
    const [F42VS, setF42VS] = useState(["", -1]);

    // NEW IOL //
    //Manufacturer / Model
    const [F51VS, setF51VS] = useState(["Select replacement IOL model", -1]);
    //A-constant
    const [F52VS, setF52VS] = useState(["", -1]);
    //Position
    const [F53VS, setF53VS] = useState(["Bag", 1]);
    //Target MRSE
    const [F54VS, setF54VS] = useState(["0.00", 1]);
    //Use toric for exchange? ("Not sure" is not active)
    const [F55VS, setF55VS] = useState(["No", 1]);

    // CASE TYPE //
    //Test / real patient calculation
    const [F61VS, setF61VS] = useState(["", -1]);

    // Every value the surgeon entered. A result is only current while this is
    // what it was calculated from; after an edit it is dimmed and offers
    // Recalculate, so an old recommendation never reads as the new one.
    const formValues = JSON.stringify([F11VS[0], F12VS[0], F21VS[0], F22VS[0], F23VS[0], F24VS[0],
        F25VS[0], F26VS[0], F31VS[0], F32VS[0], F34VS[0], F41VS[0], F42VS[0], F51VS[0], F52VS[0],
        F53VS[0], F54VS[0], F55VS[0], F61VS[0]]);
    const [calculatedValues, setCalculatedValues] = useState(null);
    const stale = (ladder !== null && !calculating && calculatedValues !== formValues);

    // Mirrors the validity checks in iol_exchange/Input.jsx's useEffect —
    // used only to tell the user WHICH fields are still missing when they
    // press Calculate too early.
    function getMissingFields(){
        let missing = [];
        // A value that is there but refused (validity 0) says what is expected.
        const field = (VS, name, rule) => (VS[1] === 0)? name + " (" + rule + ")" : name;
        if(F11VS[1] !== 1) missing.push("Change IOL model?");
        if(F12VS[1] !== 1) missing.push("A-constant source");
        if(F21VS[1] !== 1) missing.push(field(F21VS, "Current IOL power",
            CURRENT_POWER_MIN.toFixed(1) + " to " + CURRENT_POWER_MAX.toFixed(1) + " D in " + POWER_STEP + " D steps"));
        if(F23VS[1] !== 1) missing.push(field(F23VS, "Current IOL A-constant", "100 to 130"));
        if(F24VS[1] !== 1) missing.push("Current IOL position");
        if(F31VS[1] !== 1) missing.push(field(F31VS, "Sphere", "-30 to +30, up to 2 decimals"));
        if(F32VS[1] !== 1) missing.push(field(F32VS, "Cylinder", "-15 to +15, up to 2 decimals"));
        if(isSameModelChange(F11VS[0] === 'Yes', F12VS[0] === 'Model list', F22VS[0], F51VS[0])){
            missing.push('New IOL model (a different model, or set "Change IOL model?" to No)');
        }
        if(F52VS[1] !== 1) missing.push(field(F52VS, "New IOL A-constant", "100 to 130"));
        if(F53VS[1] !== 1) missing.push("New IOL position");
        if(F54VS[1] !== 1) missing.push(field(F54VS, "Target MRSE", "-15 to +15, up to 2 decimals"));
        if(F55VS[1] !== 1) missing.push("Use toric for exchange?");
        if(F55VS[0] === 'Yes'){
            if(F34VS[1] !== 1) missing.push(field(F34VS, "Refraction axis (for the toric calculation)", "0 to 180"));
            if(F25VS[1] !== 1) missing.push(field(F25VS, "Implanted IOL cylinder (0 if none)", "0 to 10, up to 2 decimals"));
            let implantedToric = !(F25VS[1] === 1 && parseFloat(F25VS[0]) === 0);
            if(implantedToric && F26VS[1] !== 1) missing.push(field(F26VS, "Implanted IOL axis of marks", "0 to 180"));
            let blocker = getToricBlocker(F55VS[0], F21VS[0], F53VS[0]);
            if(blocker) missing.push(blocker);
        }
        if(F61VS[1] !== 1) missing.push("Case type (test or real patient)");
        if(F41VS[0].includes("Other") && F42VS[1] !== 1) missing.push('"Other" reason — free text');
        return missing;
    }

    function attemptCalculate(){
        if(!validInput){
            setShowMissingPopup(true);
            return;
        }
        if(inFlight.current) return;
        inFlight.current = true;

        setShowResult(true);
        setCalculating(true);
        setCalcError(null);
        setCalculatedValues(formValues);

        calculateExchange({
            changeModel: (F11VS[0] === 'Yes'),
            currentPower: F21VS[0],
            currentAConstant: F23VS[0],
            currentPosition: F24VS[0],
            sphere: F31VS[0],
            cylinder: F32VS[0],
            newAConstant: F52VS[0],
            newPosition: F53VS[0],
            targetMRSE: F54VS[0],
            // Stored with the result, not used in the calculation.
            caseType: F61VS[0],
            constantSource: F12VS[0],
            currentModel: F22VS[0],
            newModel: F51VS[0],
            axis: F34VS[0],
            reasons: F41VS[0],
            otherReason: F42VS[0],
            useToric: F55VS[0],
            currentIolCylinder: F25VS[0],
            currentIolAxis: F26VS[0],
        })
        .then((response) => {
            if(response.ok){
                setLadder(response.result);
            }else{
                setLadder(null);
                setCalcError(response.error);
            }
            setCalculating(false);
            inFlight.current = false;
        })
    }

    if(phone){
        return (
            <div>
                <style>
                @import url('https://fonts.googleapis.com/css2?family=Titillium+Web&display=swap');
                </style>

                <BarHeader currentCalculator={"IOL Exchange"}/>
                <div className='input' id='iolx-input'>
                    <Row className='title vCenter' id='iolx-title'>
                        <h1>
                            <span className='iolx-title-inner'>
                                <FaExchangeAlt className='iolx-title-icon'/>
                                <span>IOL Exchange Calculator</span>
                            </span>
                        </h1>
                    </Row>
                    <Container className='hCenter w-90' id='iolx-container'>
                        <div className='styledBox iolx-box iolx-desktop-only'>
                            <FaDesktop className='iolx-desktop-only-icon'/>
                            <b>Available on desktop only, for now</b>
                            <span>Please open this calculator on a computer. A mobile version is on its way.</span>
                        </div>
                    </Container>
                </div>
            </div>
        )
    }

    return (
        <div>
            <style>
            @import url('https://fonts.googleapis.com/css2?family=Titillium+Web&display=swap');
            </style>

            <BarHeader currentCalculator={"IOL Exchange"}/>

            <div className={'input' + (compact? ' iolx-compact' : '')} id='iolx-input'>
                <Row className='title vCenter' id='iolx-title'>
                    <h1>
                        <span className='iolx-title-inner'>
                            <FaExchangeAlt className='iolx-title-icon'/>
                            <span>IOL Exchange Calculator</span>
                        </span>
                    </h1>
                </Row>

                <Container className='hCenter w-90' id='iolx-container'>
                    {/* View switch — aligned with the left edge of the first
                        (form) column below, not floated in a page corner. */}
                    <Row className='iolx-main-row iolx-switch-row'>
                        <Col xs={12} sm={compact? 5 : 7}>
                            <ViewSwitch compact={compact} onToggle={setCompact}/>
                        </Col>
                    </Row>

                    <Row className='iolx-main-row'>
                        {/* INPUT */}
                        <Col xs={12} sm={compact? 5 : 7}>
                            <div className={'styledBox iolx-box' + (compact? ' iolx-form-box' : '')}>
                                {/* Compact: the scrollable form and the floating Calculate
                                    button are SIBLINGS, not parent/child. The button needs
                                    to sit outside whatever scrolls — nesting it inside the
                                    scrolling area made it get clipped by that area's own
                                    overflow, no matter how it was positioned. */}
                                <div className={compact? 'iolx-compact-scroll-area' : null}>
                                    <Input
                                        validInput={validInput} setValidInput={setValidInput}
                                        onCalculate={attemptCalculate}
                                        compact={compact}

                                        F11VS={F11VS} setF11VS={setF11VS}
                                        F12VS={F12VS} setF12VS={setF12VS}

                                        F21VS={F21VS} setF21VS={setF21VS}
                                        F22VS={F22VS} setF22VS={setF22VS}
                                        F23VS={F23VS} setF23VS={setF23VS}
                                        F24VS={F24VS} setF24VS={setF24VS}
                                        F25VS={F25VS} setF25VS={setF25VS}
                                        F26VS={F26VS} setF26VS={setF26VS}

                                        F31VS={F31VS} setF31VS={setF31VS}
                                        F32VS={F32VS} setF32VS={setF32VS}
                                        F33VS={F33VS} setF33VS={setF33VS}
                                        F34VS={F34VS} setF34VS={setF34VS}

                                        F41VS={F41VS} setF41VS={setF41VS}
                                        F42VS={F42VS} setF42VS={setF42VS}

                                        F51VS={F51VS} setF51VS={setF51VS}
                                        F52VS={F52VS} setF52VS={setF52VS}
                                        F53VS={F53VS} setF53VS={setF53VS}
                                        F54VS={F54VS} setF54VS={setF54VS}
                                        F55VS={F55VS} setF55VS={setF55VS}

                                        F61VS={F61VS} setF61VS={setF61VS}
                                    />
                                </div>
                                {(compact)?
                                    <div className='iolx-compact-fab-row'>
                                        <CalculateBtn
                                            validInput={validInput}
                                            onClick={attemptCalculate}
                                        />
                                    </div>
                                : null}
                            </div>
                        </Col>

                        {(compact)?
                        <>
                            {/* COMPACT — three panels side by side, like the reference prototype */}
                            <Col xs={12} sm={3}>
                                <div className='styledBox iolx-box iolx-result-box'>
                                    <Result
                                        section='recommendation'
                                        showResult={showResult}
                                        changeModel={F11VS[0] === 'Yes'}
                                        targetMRSE={F54VS[0]}
                                        newPosition={F53VS[0]}
                                        ladder={ladder}
                                        calculating={calculating}
                                        error={calcError}
                                        stale={stale}
                                        onRecalculate={attemptCalculate}
                                    />
                                </div>
                            </Col>
                            <Col xs={12} sm={4}>
                                <div className='styledBox iolx-box iolx-result-box'>
                                    <Result
                                        section='ladder'
                                        showResult={showResult}
                                        changeModel={F11VS[0] === 'Yes'}
                                        targetMRSE={F54VS[0]}
                                        newPosition={F53VS[0]}
                                        ladder={ladder}
                                        calculating={calculating}
                                        error={calcError}
                                        stale={stale}
                                        onRecalculate={attemptCalculate}
                                    />
                                </div>
                            </Col>
                        </>
                        :
                        // LARGE — recommendation + ladder stacked in one panel
                        <Col xs={12} sm={5}>
                            <div className='styledBox iolx-box iolx-result-box'>
                                <Result
                                    section='all'
                                    showResult={showResult}
                                    changeModel={F11VS[0] === 'Yes'}
                                    targetMRSE={F54VS[0]}
                                    newPosition={F53VS[0]}
                                    ladder={ladder}
                                    calculating={calculating}
                                    error={calcError}
                                    stale={stale}
                                    onRecalculate={attemptCalculate}
                                />
                            </div>
                        </Col>
                        }
                    </Row>
                </Container>
            </div>

            <Popup
            showPopUp={showMissingPopup}
            onClick={() => setShowMissingPopup(false)}
            Icon={<BiErrorCircle style={{marginBottom: '0.65vh'}}/>}
            Title={"Missing or invalid information"}
            Body={
                <>
                    <p>Complete or correct these fields before calculating:</p>
                    <ul className='iolx-missing-list'>
                        {getMissingFields().map((field) => <li key={field}>{field}</li>)}
                    </ul>
                </>
            }
            />
        </div>
    )
}
